import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import ClimateControlField from './ClimateControlField'

type SegmentedOption = {
  value: string
  label: string
  disabled?: boolean
  disabledTooltip?: string
}

type ClimateSegmentedFieldProps = {
  label: string
  helpText?: string
  value: string
  onChange: (value: string) => void
  options: SegmentedOption[]
  disabled?: boolean
  ariaLabel: string
}

const ClimateSegmentedField = ({
  label,
  helpText,
  value,
  onChange,
  options,
  disabled = false,
  ariaLabel,
}: ClimateSegmentedFieldProps) => {
  return (
    <ClimateControlField label={label} helpText={helpText}>
      <ToggleButtonGroup
        exclusive
        fullWidth
        value={value}
        onChange={(_, next) => {
          if (next === null) return
          const nextOption = options.find(option => option.value === next)
          if (nextOption?.disabled) return
          onChange(next)
        }}
        aria-label={ariaLabel}
        sx={{
          width: '100%',
          gap: 0.5,
          p: 0.5,
          borderRadius: 2.5,
          backgroundColor: 'action.hover',
          '& .MuiToggleButtonGroup-grouped': {
            borderRadius: 2,
            margin: 0,
            border: '0 !important',
          },
          '& .MuiToggleButton-root': {
            flex: 1,
            minHeight: 40,
            px: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 14,
            lineHeight: 1.1,
            color: 'text.secondary',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'action.selected',
            },
            '&.Mui-selected': {
              color: 'text.primary',
              backgroundColor: 'background.paper',
              boxShadow: theme => theme.shadows[1],
            },
            '&.Mui-disabled': {
              opacity: 1,
              color: 'text.secondary',
              WebkitTextFillColor: 'inherit',
            },
            '&.Mui-selected.Mui-disabled': {
              color: 'text.primary',
              backgroundColor: 'background.paper',
            },
            '&[aria-disabled="true"]': {
              color: 'text.disabled',
              cursor: 'not-allowed',
            },
            '&[aria-disabled="true"]:hover': {
              backgroundColor: 'transparent',
            },
            '&.Mui-selected[aria-disabled="true"]': {
              color: 'text.disabled',
              backgroundColor: 'background.paper',
            },
          },
        }}>
        {options.map(option => {
          const optionDisabled = Boolean(option.disabled)
          const optionLabel = option.disabledTooltip ? (
            <Tooltip title={option.disabledTooltip} arrow enterTouchDelay={0}>
              <Box
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                {option.label}
              </Box>
            </Tooltip>
          ) : (
            option.label
          )

          return (
            <ToggleButton
              key={option.value}
              value={option.value}
              disabled={disabled}
              disableRipple={optionDisabled}
              aria-disabled={optionDisabled ? true : undefined}
              tabIndex={optionDisabled ? -1 : undefined}
              aria-label={option.label}>
              {optionLabel}
            </ToggleButton>
          )
        })}
      </ToggleButtonGroup>
    </ClimateControlField>
  )
}

export default ClimateSegmentedField
